import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import TextInput from '~/components/lawns/new/lawn-size-components/text-input';
import { useLawnMutations } from '~/features/lawns/lawn-actions';
import { useFunnelRequireAuth } from '~/hooks/use-funnel-require-auth';
import { myPlanRoute, newLawnRoute, neighborsFunnelPageRoute } from '~/routes';
import {
  analyticsBeacon,
  DeprecatedEventType,
  EventType,
} from '~/utils/analytics';
import { captureException } from '~/utils/exception-tracking';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { useSession } from '~/features/session/session-actions';

import layoutStyles from '~/components/layouts/layouts.module.scss';

/**
 *
 * When the user hits 'Continue'
 * 1. See if we need to pop the Small/Large Lawn modal
 *   a. Ask for sign in
 *   b. Update the Lawn with user's response
 * 2. See if we need to confirm size change (> 20% change in square footage)
 * 3. Claim Lawn
 *   a. Ask for sign in (if needed)
 *   b. Update the lawn to "claim" it
 * 4. Redirect to Lawn Details page
 */

const LawnSize = ({ lawn, showModal, setLawn }) => {
  const { showFlashMessage } = useFlashMessage();
  const { getUser } = useSession();
  const { claimLawn, updateLawn } = useLawnMutations();

  const [isUpdatingLawn, setIsUpdatingLawn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    analyticsBeacon.emit(DeprecatedEventType.START_LAWN_FUNNEL, 'Lawn Plan');
    analyticsBeacon.emit(DeprecatedEventType.LAWN_FUNNEL_STEP, 'New Lawn');
  }, []);

  const { requireAuth } = useFunnelRequireAuth({
    onModalClose: () => setIsUpdatingLawn(false),
  });

  /**
   * When users update a lawn we want to capture the ID in the URL
   * so that page reloads and the back button will load the existing
   * lawn instead of creating a new one
   */
  const storeLawnUuid = (lawnUuid) => {
    return navigate(newLawnRoute({ lawnUuid }), {
      replace: true,
    });
  };

  /**
   * Users are only allowed to have a single active lawn, so if they
   * try to "claim" another by sending a `PUT /lawns/:uuid` request
   * the API returns an error. When we see that error redirect to the
   * account page and show a flash message
   */
  const handleUpdateLawnError = (err, message) => {
    captureException(new UpdateLawnError(message), {
      extras: {
        error: JSON.stringify(err),
      },
    });
    if (isExistingPlanError(err)) {
      showFlashMessage(
        'Sorry, we only support one lawn plan subscription per account.',
        'info'
      );
      return navigate(myPlanRoute());
    } else {
      setIsUpdatingLawn(false);
      showModal('ERROR');
    }
  };

  const handleConfirmSizeEstimate = (lawn) => {
    setIsUpdatingLawn(true);
    return claimLawn(lawn.uuid)
      .then((lawn) => {
        setLawn(lawn);
        return lawn;
      })
      .then((lawn) => {
        storeLawnUuid(lawn.uuid);
        return lawn;
      })
      .then((lawn) => {
        return navigate(
          neighborsFunnelPageRoute({
            postalCode: lawn.address?.postalCode,
            latitude: lawn.address?.latitude,
            longitude: lawn.address?.longitude,
            lawnUuid: lawn.uuid,
          })
        );
      })
      .catch((err) => handleUpdateLawnError(err, 'Claim Lawn Error'));
  };

  const updateLawnSize = (size) => {
    return updateLawn(lawn.uuid, {
      size: {
        sqFt: size,
      },
    })
      .then((lawn) => {
        setLawn(lawn);
        storeLawnUuid(lawn.uuid);
        return lawn;
      })
      .catch((err) => {
        handleUpdateLawnError(err, 'Update Lawn Size Error');
        return err;
      });
  };

  const continueToNextPage = async (newLawnSize) => {
    await requireAuth();

    let updatedLawn = lawn;
    if (parseInt(newLawnSize) !== lawn.size.sqFt) {
      updatedLawn = await updateLawnSize(newLawnSize);
      const user = getUser();

      if (isExistingPlanError(updatedLawn)) {
        return;
      }

      analyticsBeacon.emit(EventType.LAWN_SIZE_UPDATED, {
        lawn,
        userUuid: user?.uuid,
        newLawnSize: parseInt(newLawnSize),
      });
    }

    await handleConfirmSizeEstimate(updatedLawn);
  };

  const handleContinue = async (newLawnSize) => {
    if (newLawnSize < lawn.size.smallLawnThreshold) {
      setIsUpdatingLawn(true);
      showModal('SMALL_LAWN', {
        lawn,
        newLawnSize,
        onContinue: async () => {
          await continueToNextPage(newLawnSize);
        },
        onClose: () => {
          setIsUpdatingLawn(false);
        },
      });
    } else if (newLawnSize > lawn.size.largeLawnThreshold) {
      setIsUpdatingLawn(true);
      showModal('LARGE_LAWN', {
        lawn,
        newLawnSize,
        onContinue: async () => {
          await continueToNextPage(newLawnSize);
        },
        onClose: () => {
          setIsUpdatingLawn(false);
        },
      });
    } else {
      setIsUpdatingLawn(true);
      continueToNextPage(newLawnSize);
    }
  };

  const handleSomethingsNotRight = () => {
    showModal('SOMETHINGS_NOT_RIGHT');
  };

  return (
    <div className={layoutStyles.sidebarContainer}>
      <TextInput
        lawn={lawn}
        onContinue={handleContinue}
        onSomethingsNotRight={handleSomethingsNotRight}
        isSubmitting={isUpdatingLawn}
      />
    </div>
  );
};

export default LawnSize;

export function isExistingPlanError(err) {
  return (
    err?.detail ===
    'User has paid Subscription and is unable to update their lawn.'
  );
}

export class UpdateLawnError extends Error {
  constructor(message) {
    super(message);
    this.name = 'UpdateLawnError';
  }
}
